import { Injectable, signal, OnDestroy, inject } from '@angular/core';
import { take } from 'rxjs/operators';
import { SessionService } from '@modules/auth/services/session.service';
import { environment } from '@src/environments/environment';
import { EventSourcePolyfill } from 'event-source-polyfill';

export type SseEventType =
  | 'inquiry.new'
  | 'inquiry.new_message'
  | 'inquiry.closed'
  | 'conversation.new'
  | 'conversation.new_message'
  | 'conversation.closed'
  | 'presence.typing_start'
  | 'presence.typing_stop'
  | 'presence.unread_count_changed';

export interface SseEvent {
  type: SseEventType;
  threadId?: string;
  unreadCount?: number;
  totalUnread?: number;
  senderType?: 'guest' | 'host';
  guestName?: string;
  guestEmail?: string;
  propertyName?: string;
  messagePreview?: string;
}

const SSE_EVENT_TYPES: SseEventType[] = [
  'inquiry.new',
  'inquiry.new_message',
  'inquiry.closed',
  'conversation.new',
  'conversation.new_message',
  'conversation.closed',
  'presence.typing_start',
  'presence.typing_stop',
  'presence.unread_count_changed',
];

/** Typed alias to avoid repeated unsafe casts throughout the service */
type TypedEventSource = EventSource & { close(): void };

@Injectable({
  providedIn: 'root',
})
export class ConversationsSseService implements OnDestroy {
  private readonly sessionService = inject(SessionService);

  private eventSource: EventSourcePolyfill | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly maxReconnectAttempts = 10;
  /** Blokada przed równoległym refreshem tokena przy nawiązywaniu SSE */
  private refreshing = false;

  private readonly _unreadCount = signal(0);
  readonly unreadCount = this._unreadCount.asReadonly();

  private readonly _lastEvent = signal<SseEvent | null>(null);
  readonly lastEvent = this._lastEvent.asReadonly();

  /** Set of threadIds where the guest is currently typing */
  private readonly _typingThreadIds = signal<Set<string>>(new Set());
  readonly typingThreadIds = this._typingThreadIds.asReadonly();

  private readonly _connected = signal(false);
  readonly connected = this._connected.asReadonly();

  private typingTimers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor() {
    // Po powrocie apki na pierwszy plan / odzyskaniu sieci nie czekaj na
    // backoff reconnectu — wznów strumień od razu. connect() sam sprawdza,
    // czy jest sesja, więc po wylogowaniu to no-op.
    document.addEventListener('visibilitychange', this.restoreConnection);
    window.addEventListener('online', this.restoreConnection);
  }

  private readonly restoreConnection = (): void => {
    if (document.visibilityState === 'hidden') {
      return;
    }
    if (this.eventSource) {
      return; // strumień otwarty — ewentualny zgon wykryje heartbeatTimeout
    }
    // Wyczyść zaplanowany backoff i połącz od razu, ale NIE zeruj
    // reconnectAttempts — gdyby backend był w realnej awarii, zerowanie przy
    // każdym foregroundzie/online resetowałoby backoff do 1 s w kółko.
    // Udane onopen i tak wyzeruje licznik.
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.connect();
  };

  connect(): void {
    if (this.eventSource) {
      return; // Already connected
    }

    const token = this.sessionService.getAccessToken();
    if (!token) {
      return; // Brak sesji — nie łączymy
    }

    // Access token (15 min) bywa wygasły przy starcie po dłuższej nieaktywności.
    // SSE (EventSource) NIE przechodzi przez TokenRefreshInterceptor, więc nie dostaje
    // automatycznego refresh+retry — z wygasłym tokenem backend zwróciłby 401, a my
    // wpadlibyśmy w pętlę reconnectów z tym samym martwym tokenem (aż coś innego
    // odświeży sesję). Dlatego najpierw upewniamy się, że token jest ważny: jeśli
    // wygasł — odśwież (dedup w SessionService) i dopiero wtedy otwórz strumień.
    if (!this.sessionService.isTokenValid()) {
      if (this.refreshing) {
        return; // refresh już w toku — po sukcesie sam otworzy strumień
      }
      this.refreshing = true;
      this.sessionService
        .refreshToken()
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.refreshing = false;
            const fresh = this.sessionService.getAccessToken();
            if (fresh) {
              this.openStream(fresh);
            }
          },
          error: () => {
            // Refresh padł (np. refresh token martwy) — nie reconnectujemy;
            // zwykły flow auth wyloguje i przekieruje na login.
            this.refreshing = false;
          },
        });
      return;
    }

    this.openStream(token);
  }

  private openStream(token: string): void {
    if (this.eventSource) {
      return; // Already connected
    }

    const url = `${environment.apiUrl}events`;

    const polyfillEs = new EventSourcePolyfill(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      // Backend wysyła heartbeat co 15 s — 35 s daje margines 20 s (tolerancja
      // jednego pominiętego bicia + jitter LTE), a i tak wykrywa martwe
      // połączenie (iOS zamraża sockety) w ≤35 s, dużo szybciej niż domyślne 45 s.
      heartbeatTimeout: 35000,
    });

    // Cast once to typed alias for all subsequent event handler assignments
    const es = polyfillEs as unknown as TypedEventSource;

    es.onopen = () => {
      this.reconnectAttempts = 0;
      this._connected.set(true);
    };

    // Listen for named SSE event types (backend sends `event: inquiry.new_message`)
    for (const eventType of SSE_EVENT_TYPES) {
      es.addEventListener(eventType, ((event: MessageEvent) => {
        this.handleSseData(event.data, eventType);
      }) as EventListener);
    }

    // Fallback: also listen for unnamed/default message events
    es.onmessage = (event: MessageEvent) => {
      this.handleSseData(event.data);
    };

    es.onerror = () => {
      this._connected.set(false);
      this.eventSource?.close();
      this.eventSource = null;
      this.scheduleReconnect();
    };

    this.eventSource = polyfillEs;
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.reconnectAttempts = 0;

    // Clear typing timers
    for (const timer of this.typingTimers.values()) {
      clearTimeout(timer);
    }
    this.typingTimers.clear();
    this._typingThreadIds.set(new Set());
  }

  setUnreadCount(count: number): void {
    this._unreadCount.set(count);
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.restoreConnection);
    window.removeEventListener('online', this.restoreConnection);
    this.disconnect();
  }

  private handleSseData(raw: string, namedType?: SseEventType): void {
    try {
      const payload = JSON.parse(raw);

      // New format: { namespace, type, threadId, data: {...} }
      // Old format: { type, threadId, ... }
      const eventType: SseEventType = namedType ?? (
        payload.namespace && payload.type
          ? `${payload.namespace}.${payload.type}` as SseEventType
          : payload.type
      );
      const threadId: string | undefined = payload.threadId ?? payload.data?.threadId;
      const data = payload.data ?? payload;

      const sseEvent: SseEvent = {
        type: eventType,
        threadId,
        unreadCount: data.unreadCount,
        totalUnread: data.totalUnread,
        senderType: data.senderType,
        guestName: data.guestName,
        guestEmail: data.guestEmail,
        propertyName: data.propertyName,
        messagePreview: data.messagePreview,
      };

      // Handle presence events directly — do NOT set _lastEvent for them.
      // Angular effects are async: if a presence event arrives right after
      // an inquiry/conversation event (within the same microtask), it would
      // overwrite _lastEvent before the effect runs, causing the real event
      // to be lost.
      if (eventType === 'presence.typing_start' && threadId) {
        this.setTyping(threadId, true);
        return;
      }
      if (eventType === 'presence.typing_stop' && threadId) {
        this.setTyping(threadId, false);
        return;
      }
      if (eventType === 'presence.unread_count_changed') {
        if (sseEvent.totalUnread !== undefined) {
          this._unreadCount.set(sseEvent.totalUnread);
        }
        return;
      }

      // Only set _lastEvent for inquiry/conversation events
      this._lastEvent.set(sseEvent);

      // Handle conversation/inquiry events (existing logic)
      if (sseEvent.unreadCount !== undefined) {
        this._unreadCount.set(sseEvent.unreadCount);
      } else if (
        sseEvent.type === 'inquiry.new' ||
        sseEvent.type === 'inquiry.new_message' ||
        sseEvent.type === 'conversation.new' ||
        sseEvent.type === 'conversation.new_message'
      ) {
        this._unreadCount.update((count) => count + 1);
      }
    } catch (error) {
      console.warn('Failed to parse SSE data:', error);
    }
  }

  private setTyping(threadId: string, isTyping: boolean): void {
    // Clear existing timer for this thread
    const existingTimer = this.typingTimers.get(threadId);
    if (existingTimer) {
      clearTimeout(existingTimer);
      this.typingTimers.delete(threadId);
    }

    if (isTyping) {
      this._typingThreadIds.update((set) => {
        const next = new Set(set);
        next.add(threadId);
        return next;
      });
      // Auto-clear typing after 5s (safety timeout)
      const timer = setTimeout(() => {
        this.setTyping(threadId, false);
      }, 5000);
      this.typingTimers.set(threadId, timer);
    } else {
      this._typingThreadIds.update((set) => {
        const next = new Set(set);
        next.delete(threadId);
        return next;
      });
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}
